import { observable } from "mobx";
import OutageScheduleReportAPI from "../services/outage-schedule-report-service";
import { AppConstants, Types } from "../../../common/APIConstants";
import RestEndPoint from "../../../common/RestEndPoint";

class OutageScheduleUserStore {
  @observable colDefs = []
  @observable rowData = []
  @observable initialValues = {
    ID: '',
    GroupId: '',
    UserName: '',
    EmailId: '',
    PhoneNumber: '',
    IsActive: '',
    Region: '',
    UpdatedBy: '',
    UpdatedOn: '',
    CreatedBy: '',
    CreatedOn: ''
  }
  @observable ufmColDefs = []
  @observable ufmRowData = []
  @observable regions = [
    {
      value: 'All',
      label: 'All'
    },
    {
      value: 'PN',
      label: 'Plains - North'
    },
    {
      value: 'PS',
      label: 'Plains - South'
    }]

  getColumnFilter(column) {
    switch (column.type) {
      case Types.DATETIME:
        return 'agDateColumnFilter'
      case Types.NUMBER:
        return 'agNumberColumnFilter'
      case Types.TEXT:
        return 'agTextColumnFilter'
      default:
        return 'agSetColumnFilter'
    }
  }

  getCellRenderer(column, isMappingGrid) {
    if (['IsAcknowledger',
      'SendPlannedNotification',
      'SendUnPlannedNotification',
      'SendSMS',
      'IsActive'
    ].includes(column.name) && isMappingGrid) {
      return 'booleanCellEditor'
    }
    if (['CreatedOn', 'UpdatedOn'].includes(column.name)) {
      return 'dateFormatter'
    }
    if (column.type === Types.BOOLEAN && !isMappingGrid) {
      return 'booleanTextFormatter'
    }
  }

  getLookupValue = (column, params) => {
    let matchPair = null
    if (column?.name === 'GroupId') {
      const lookupValues = column?.refLookupData?.lookupValues?.values
      const cellValue = params?.value
      if (lookupValues?.length && cellValue) {
        matchPair = lookupValues.find(pair =>
          parseInt(pair?.value) === cellValue
        )
      }
    }
    return matchPair?.label
  }

  getValueFormatter = (column, params, isMappingGrid) => {
    if (column?.type === Types.DATETIME) {
      return 'dateTimeFormatter'
    }
    if (column?.type === Types.BOOLEAN) {
      return (params.value === true || params.value === 'true') ? 'Yes' : 'No'
    }
    if (column?.name === 'FacilityId' && isMappingGrid) {
      return params?.data?.Asset || ''
    }
    if (column.type === Types.DROPDOWN && column?.refLookupData) {
      return this.getLookupValue(column, params);
    }
  }

  getGridColumns = (currentScreen) => {
    const { screenColumns, screen } = currentScreen
    const isMappingGrid = screen.name === 'OutageUserFacilityMapping'
    const colDefs = [...screenColumns].map(column => {
      return {
        field: column.name,
        headerName: column.title,
        hide: column.hidden,
        type: column?.type || 'text',
        width: column?.width || 200,
        cellClassRules: {
          'grid-cell-custom': params => params?.data ? true : false
        },
        keyCreator: (params) => {
          if (['GroupId', 'FacilityId'].includes(column.name))
            return this.getValueFormatter(column, params, isMappingGrid)
          return params.value
        },
        cellRenderer: this.getCellRenderer(column, isMappingGrid),
        cellRendererParams: params => this.cellRendererParams(params),
        filter: this.getColumnFilter(column),
        valueFormatter: params => this.getValueFormatter(column, params, isMappingGrid)
      }
    })
    if (isMappingGrid) {
      this.ufmColDefs = colDefs
    } else {
      this.colDefs = colDefs
    }
  }

  getGridData = async (currentScreen, CommonStore) => {
    CommonStore.toggleLoader()
    try {
      const { data } = await OutageScheduleReportAPI.getScreenData({
        screenDetails: currentScreen
      })
      this.rowData = data
      CommonStore.toggleLoader()
    } catch (err) {
      CommonStore.toggleLoader()
      window.toastr.error(AppConstants.TOASTER_ERROR_SAVE_TEXT);
    }
  }

  getCurrentScreen = (path, screenDetails) => {
    let screenName = path.split("/").pop()
    const currScreen = screenDetails.find(item => item.screen.name === screenName)
    if (currScreen) {
      return currScreen
    }
    return null
  }

  async saveScreenData(data, commonStore, currentScreen, action,
    ufmGridOptions, isAddMode) {
    const currentUser = RestEndPoint.getCurrentUserName();
    let savedUserId = data.ID
    const payload = {}
    Object.assign(payload, currentScreen)
    payload.data = data
    payload.data.UpdatedBy = currentUser
    payload.data.UpdatedOn = new Date()

    if (isAddMode) {
      payload.data.CreatedBy = currentUser
      payload.data.CreatedOn = new Date()
    }

    try {
      if (action === 'Save') {
        const { data } = await OutageScheduleReportAPI.saveUserData(payload);
        savedUserId = data
      }
      if (action === 'Update') {
        await OutageScheduleReportAPI.updateUserData(payload);
      }
      this.saveUserFacilityMappingData(ufmGridOptions, isAddMode, savedUserId, commonStore)
    } catch (err) {
      window.toastr.error(AppConstants.TOASTER_ERROR_SAVE_TEXT);
    }
  }

  /* User Facility Mapping */
  cellRendererParams = (params) => {
    if (params?.data?.Region === 'Plains - South' &&
      params?.column?.colId === 'IsAcknowledger') {
      return { isDisabled: true }
    }
  }

  async getUfmRowData(currentScreen, userId, selectedRegion) {
    currentScreen.screenFilters[0].value = selectedRegion
    let requestObj = {
      "ScreenDetails": currentScreen,
      UserId: userId
    }
    const { data } = await OutageScheduleReportAPI.getScreenData(requestObj)
    this.ufmRowData = data.map(item => {
      return {
        ...item,
        IsAcknowledger: item?.IsAcknowledger === 1 || false,
        SendPlannedNotification: item?.SendPlannedNotification === 1 || false,
        SendUnPlannedNotification: item?.SendUnPlannedNotification === 1 || false,
        SendSMS: item?.SendSMS === 1 || false,
        IsActive: item?.IsActive === 1 || false,
      }
    })
  }

  async saveUserFacilityMappingData(ufmGridOptions, isAddMode, userId, commonStore) {
    const data = []
    const currentUser = RestEndPoint.getCurrentUserName();

    ufmGridOptions.api.forEachNode(rowNode => {
      rowNode.data.UserId = userId
      rowNode.data.UpdatedBy = currentUser
      rowNode.data.UpdatedOn = new Date()
      if (isAddMode) {
        rowNode.data.CreatedBy = currentUser;
        rowNode.data.CreatedOn = new Date();
      }
      data.push(rowNode.data);
    })
    const payload = {
      UserFacilityMappings: data,
      UserId: userId
    }

    try {
      await OutageScheduleReportAPI.SaveUserFacilityMappingData(payload);
      window.toastr.success(AppConstants.TOASTER_SUCCESS);
    }
    catch (error) {
      window.toastr.error(AppConstants.TOASTER_ERROR_SAVE_TEXT);
    }
  };
}

export default new OutageScheduleUserStore()